import { useState, useEffect, useMemo, useRef } from 'react';
import { cn } from '../../../lib/cn';
import { logger } from '../../../lib/logger';
import { useKeystoneConversations } from './useKeystoneConversations';
import { PlusIcon, MessageSquareIcon } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ConversationListProps {
  accountId: string;
  currentId: string | null;
  onSelect: (id: string) => void;
  onNew: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Coerce a string|Date|null to string|null (API responses are always
 * strings). Exported for `ChatConversationDropdown.tsx` (U6) — the Flow
 * dock's conversation-history dropdown reuses this rather than forking a
 * second copy (see `plans/flow-global-chat-dock/coding-standards.md` §1).
 */
export function toDateString(v: string | Date | null): string | null {
  if (v === null) return null;
  return typeof v === 'string' ? v : v.toISOString();
}

/**
 * Relative timestamp like "2m ago", "3h ago", "yesterday", etc. Exported for
 * `ChatConversationDropdown.tsx` (U6) — same reuse rationale as
 * `toDateString` above.
 */
export function relativeTime(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = Date.now();
  const diffMs = now - date.getTime();
  if (diffMs < 0) return 'just now';

  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;

  return `${Math.floor(months / 12)}y ago`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ConversationList({
  accountId,
  currentId,
  onSelect,
  onNew,
}: ConversationListProps) {
  const query = useKeystoneConversations();
  const { refetch } = query;
  const [accountNotFound, setAccountNotFound] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Client-side filter: only show conversations for this account
  const conversations = useMemo(
    () => (query.data ?? []).filter((c) => c.selectedAccountId === accountId),
    [query.data, accountId],
  );
  const loading = query.isPending;
  const error = createError ?? (query.isError ? query.error.message : null);

  useEffect(() => {
    if (query.isError) {
      logger.error({ err: query.error }, '[ConversationList] fetch error');
    }
  }, [query.isError, query.error]);

  // Re-fetch when currentId changes (covers create / delete from parent). The
  // hook's `refetchOnMount: 'always'` covers the initial load.
  const lastCurrentId = useRef(currentId);
  useEffect(() => {
    if (lastCurrentId.current === currentId) return;
    lastCurrentId.current = currentId;
    void refetch();
  }, [currentId, refetch]);

  function retry() {
    setCreateError(null);
    void refetch();
  }

  // ----- New conversation ----------------------------------------------------

  async function handleNew() {
    setCreating(true);
    setCreateError(null);
    try {
      await onNew();
      await refetch();
    } catch (err: any) {
      if (err?.status === 404 && err?.message === 'Account not found') {
        setAccountNotFound(true);
      } else {
        setCreateError(err?.message ?? 'Failed to create conversation');
      }
    } finally {
      setCreating(false);
    }
  }

  // ----- Render --------------------------------------------------------------

  return (
    <nav className="flex h-full flex-col gap-1 overflow-y-auto p-2 bg-bg-raised/50 backdrop-blur-md">
      {/* New Conversation button — only shown when conversations exist (empty state is the entry point otherwise) */}
      {!accountNotFound && !loading && conversations.length > 0 && (
        <button
          className="mb-2 flex h-9 w-full items-center justify-start gap-2 rounded-xl border border-border-secondary bg-bg-raised/85 px-3 text-sm font-semibold text-content-primary hover:bg-bg-raised shadow-sm disabled:opacity-50 transition-colors"
          onClick={() => { void handleNew(); }}
          disabled={creating}
        >
          <PlusIcon className="size-4" />
          {creating ? 'Creating...' : 'New Conversation'}
        </button>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="flex flex-col gap-1">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="flex h-14 items-center px-3">
              <div className="h-4 w-full animate-pulse rounded bg-bg-tertiary" />
            </div>
          ))}
        </div>
      )}

      {/* Account not found — non-retryable */}
      {!loading && accountNotFound && (
        <div className="px-3 py-4 text-center text-sm text-icon-subtle">
          <p>This account hasn&apos;t been imported into Keystone yet, please contact Ops</p>
        </div>
      )}

      {/* Error state */}
      {!loading && !accountNotFound && error && (
        <div className="px-3 py-4 text-center text-sm text-red-500">
          <p>{error}</p>
          <button
            onClick={retry}
            className="mt-2 text-xs underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && conversations.length === 0 && (
        <div className="flex flex-col items-center justify-center px-3 py-8 text-center text-icon-subtle">
          <MessageSquareIcon className="mb-2 size-8 opacity-50" />
          <p className="text-sm font-medium">No conversations yet</p>
          <p className="mt-1 text-xs">
            Click &quot;New Conversation&quot; to get started.
          </p>
        </div>
      )}

      {/* Conversation items */}
      {!loading &&
        !error &&
        conversations.map((conv) => {
          const isActive = conv.id === currentId;
          return (
            <button
              key={conv.id}
              onClick={() => onSelect(conv.id)}
              className={cn(
                'group flex w-full flex-col items-start gap-0.5 rounded-lg px-3 py-2 text-left text-sm transition-colors focus-visible:outline-none',
                isActive
                  ? 'bg-bg-raised/90 border border-border-secondary shadow-sm font-semibold'
                  : 'text-content-secondary hover:bg-bg-raised/50',
              )}
            >
              {/* Title row */}
              <span className="w-full truncate text-content-primary">
                {conv.title || 'New Conversation'}
              </span>

              {/* Meta row: timestamp */}
              <span className="flex w-full items-center gap-1.5 text-xs text-icon-subtle">
                <span className="ml-auto whitespace-nowrap">
                  {relativeTime(toDateString(conv.updatedAt ?? conv.createdAt))}
                </span>
              </span>
            </button>
          );
        })}
    </nav>
  );
}
